import React, { useState, useEffect, useCallback } from 'react';
import { 
  Menu, X, MapPin, Palette, Pin, GripVertical, Filter,
  LogIn, Plus, Trash2, LogOut, Loader2, Edit2, Upload, ChevronLeft, ChevronRight,
  Phone, Mail, GraduationCap, Users, Shield, Target, Clock, Star, UserCheck, Swords, BookOpen, Instagram,
  Facebook
} from 'lucide-react';

// --- THEME SYSTEM ---
const THEMES = {
  'klassisch-rot': {
    name: 'Klassisch Rot',
    emoji: '🥋',
    desc: 'Traditionelles Karate – Die Sonne Japans',
    colors: { 50: '254 242 242', 100: '254 226 226', 200: '254 202 202', 400: '248 113 113', 500: '239 68 68', 600: '220 38 38', 700: '185 28 28', 800: '153 27 27', 900: '127 29 29' },
    preview: '#dc2626'
  },
  'okinawa-blau': {
    name: 'Okinawa Blau',
    emoji: '🌊',
    desc: 'Das Meer von Okinawa – Wiege des Karate',
    colors: { 50: '239 246 255', 100: '219 234 254', 200: '191 219 254', 400: '96 165 250', 500: '59 130 246', 600: '37 99 235', 700: '29 78 216', 800: '30 64 175', 900: '30 58 138' },
    preview: '#2563eb'
  },
  'dojo-gruen': {
    name: 'Dojo Grün',
    emoji: '🎋',
    desc: 'Bambus & Natur – Ruhe und Stärke',
    colors: { 50: '236 253 245', 100: '209 250 229', 200: '167 243 208', 400: '52 211 153', 500: '16 185 129', 600: '5 150 105', 700: '4 120 87', 800: '6 95 70', 900: '6 78 59' },
    preview: '#059669'
  },
  'sonnenuntergang': {
    name: 'Sonnenuntergang',
    emoji: '🌅',
    desc: 'Warmes Abendlicht über dem Dojo',
    colors: { 50: '255 247 237', 100: '255 237 213', 200: '254 215 170', 400: '251 146 60', 500: '249 115 22', 600: '234 88 12', 700: '194 65 12', 800: '154 52 18', 900: '124 45 18' },
    preview: '#ea580c'
  },
  'sakura': {
    name: 'Sakura',
    emoji: '🌸',
    desc: 'Kirschblüte – Schönheit & Vergänglichkeit',
    colors: { 50: '253 242 248', 100: '252 231 243', 200: '251 207 232', 400: '244 114 182', 500: '236 72 153', 600: '219 39 119', 700: '190 24 93', 800: '157 23 77', 900: '131 24 67' },
    preview: '#db2777'
  },
  'kuro-obi': {
    name: 'Kuro Obi',
    emoji: '🏅',
    desc: 'Der goldene Weg des schwarzen Gürtels',
    colors: { 50: '254 252 232', 100: '254 249 195', 200: '254 240 138', 400: '250 204 21', 500: '234 179 8', 600: '202 138 4', 700: '161 98 7', 800: '133 77 14', 900: '113 63 18' },
    preview: '#ca8a04'
  }
};

const applyTheme = (themeKey) => {
  const theme = THEMES[themeKey] || THEMES['klassisch-rot'];
  const root = document.documentElement;
  Object.entries(theme.colors).forEach(([key, value]) => {
    root.style.setProperty(`--theme-${key}`, value);
  });
};

// Icon helper: maps icon name string to React component
const iconComponents = { GraduationCap, Users, Shield, Star, Target, BookOpen, UserCheck, Clock, Swords, Phone, Mail, MapPin, Instagram, Facebook };
const iconNames = Object.keys(iconComponents);
const getIcon = (name, size = 28) => {
  const Icon = iconComponents[name] || Star;
  return <Icon size={size} />;
};

// Supabase Configuration
const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;

const defaultPages = {
  karate: {
    title: "Karate Do",
    content: `Karate-Do – Der Weg der leeren Hand

Karate ist weit mehr als eine Kampfkunst. Es ist ein Weg der Selbstdisziplin, der körperlichen und geistigen Entwicklung. Im Shotokan-Karate trainieren wir traditionelle Techniken: Kihon (Grundtechniken), Kata (Formen) und Kumite (Partnerübungen).

Die Philosophie des Karate-Do betont Respekt, Bescheidenheit und ständige Selbstverbesserung. Diese Werte sind das Fundament unseres Trainings und begleiten unsere Karateka auf und außerhalb der Matte.

Ob Anfänger oder Fortgeschrittener – bei uns findet jeder den richtigen Einstieg. Unser Training ist für alle Altersgruppen geeignet und wird von erfahrenen Trainern geleitet.`,
    image_url: "/funakoshi.jpg",
    subtitle: "Gichin Funakoshi\nBegründer des modernen Karate-Do"
  },
  'privattraining': {
    title: "Privattraining",
    content: `Unser Privattraining bietet Ihnen die exklusive Möglichkeit, intensiv und individuell an Ihren Fähigkeiten zu arbeiten. Egal ob Anfänger, der die Grundlagen erlernen möchte, oder Fortgeschrittener, der seine Technik perfektionieren will.`,
    image_url: "",
    subtitle: "Exklusives 1-zu-1 Training für maximalen Fortschritt"
  },
  'selbstverteidigungskurs': {
    title: "Selbstverteidigungskurs",
    content: `In diesem Kurs geht es nicht um sportlichen Wettkampf nach Regeln, sondern um den reinen Selbstschutz und die eigene Sicherheit in kritischen Straßen-Situationen. Keine Vorkenntnisse erforderlich.`,
    image_url: "",
    subtitle: "Praxisnahe Selbstverteidigung for reale Situationen"
  },
  'kontakt': {
    title: "Kontakt",
    content: `TuS International Bremen e.V.
Bremen, Deutschland
info@tus-international.de
+49 123 456 7890
@tus_international_bremen
https://www.facebook.com/tusbremen`,
    image_url: "",
    subtitle: "Wir freuen uns auf Ihre Nachricht"
  },
  'hero': {
    title: "TuS International Bremen",
    content: "Traditionelles Karate-Do seit 2003",
    image_url: "https://images.unsplash.com/photo-1555597673-b21d5c935865?auto=format&fit=crop&q=80",
    subtitle: ""
  },
  'privattraining-hero': {
    title: "1-zu-1 Privattraining",
    content: "",
    subtitle: "",
    image_url: ""
  },
  'privattraining-cta': {
    title: "Vereinbaren Sie Ihren persönlichen Termin",
    content: "Preise auf Anfrage",
    subtitle: "Jetzt anfragen",
    image_url: ""
  },
  'selbstverteidigungskurs-hero': {
    title: "Echte Selbstverteidigung",
    content: "",
    subtitle: "",
    image_url: ""
  },
  'selbstverteidigungskurs-cta': {
    title: "Bereit für Ihren ersten Kurs?",
    content: "Kontaktieren Sie uns für Kurszeiten und Anmeldungsmöglichkeiten.",
    subtitle: "Jetzt anmelden",
    image_url: ""
  },
  'kontakt-cta': {
    title: "Probetraining vereinbaren",
    content: "Sie möchten Karate ausprobieren? Vereinbaren Sie ein kostenloses Probetraining und lernen Sie unseren Verein kennen. Wir freuen uns auf Sie!",
    subtitle: "",
    image_url: ""
  }
};

// Multi-Image Upload Field Component
const MultiImageUploadField = ({ value, onChange, supabaseClient }) => {
  const [uploading, setUploading] = useState(false);
  const [images, setImages] = useState([]);

  useEffect(() => {
    try {
      if (value) {
        if (value.startsWith('[') && value.endsWith(']')) {
          setImages(JSON.parse(value));
        } else {
          setImages([value]);
        }
      } else {
        setImages([]);
      }
    } catch (e) {
      setImages(value ? [value] : []);
    }
  }, [value]);

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length || !supabaseClient) return;

    setUploading(true);
    const newImages = [...images];

    for (const file of files) {
      try {
        const fileExt = file.name.split('.').pop();
        const fileName = `${Date.now()}_${Math.random().toString(36).substring(2, 8)}.${fileExt}`;
        const filePath = fileName;

        const { error: uploadError } = await supabaseClient.storage
          .from('images')
          .upload(filePath, file, { upsert: true });

        if (uploadError) {
          alert('Upload fehlgeschlagen: ' + uploadError.message);
          continue;
        }

        const { data: urlData } = supabaseClient.storage
          .from('images')
          .getPublicUrl(filePath);

        newImages.push(urlData.publicUrl);
      } catch (err) {
        alert('Upload fehlgeschlagen: ' + err.message);
      }
    }

    setImages(newImages);
    onChange(JSON.stringify(newImages));
    setUploading(false);
  };

  const removeImage = (index) => {
    const newImages = images.filter((_, i) => i !== index);
    setImages(newImages);
    onChange(JSON.stringify(newImages));
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3">
        {images.map((img, idx) => (
          <div key={idx} className="relative aspect-video rounded-xl overflow-hidden bg-gray-100 border border-gray-200 shadow-sm">
            <img src={img} alt="Vorschau" className="w-full h-full object-cover" />
            <button
              type="button"
              onClick={() => removeImage(idx)}
              className="absolute top-2 right-2 bg-[rgb(var(--theme-600))] text-white p-1 rounded-full hover:opacity-90 shadow-md"
            >
              <X size={12} />
            </button>
          </div>
        ))}
      </div>
      
      <label className={`flex items-center justify-center gap-2 w-full p-4 rounded-xl border-2 border-dashed cursor-pointer transition-all ${
        uploading ? 'border-gray-300 bg-gray-50 cursor-wait' : 'border-gray-300 bg-gray-50 hover:border-[rgb(var(--theme-400))] hover:bg-[rgb(var(--theme-50))]'
      }`}>
        {uploading ? (
          <><Loader2 size={20} className="animate-spin text-[rgb(var(--theme-600))]" /> <span className="text-sm font-bold text-gray-500 italic">Hochladen...</span></>
        ) : (
          <><Upload size={20} className="text-gray-400" /> <span className="text-sm font-bold text-gray-500 uppercase tracking-wider">{images.length > 0 ? 'Weitere Bilder' : 'Bilder hochladen'}</span></>
        )}
        <input
          type="file"
          multiple
          accept="image/jpeg,image/png,image/gif,image/webp"
          className="hidden"
          onChange={handleFileUpload}
          disabled={uploading}
        />
      </label>
    </div>
  );
};

// Image Upload Field Component
const ImageUploadField = ({ value, onChange, required, supabaseClient }) => {
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(value || '');

  useEffect(() => {
    setPreview(value || '');
  }, [value]);

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file || !supabaseClient) return;

    setUploading(true);
    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `${Date.now()}_${Math.random().toString(36).substring(2, 8)}.${fileExt}`;
      const filePath = fileName;

      const { error: uploadError } = await supabaseClient.storage
        .from('images')
        .upload(filePath, file, { upsert: true });

      if (uploadError) {
        alert('Upload fehlgeschlagen: ' + uploadError.message);
        setUploading(false);
        return;
      }

      const { data: urlData } = supabaseClient.storage
        .from('images')
        .getPublicUrl(filePath);

      const publicUrl = urlData.publicUrl;
      setPreview(publicUrl);
      onChange(publicUrl);
    } catch (err) {
      alert('Upload fehlgeschlagen: ' + err.message);
    }
    setUploading(false);
  };

  return (
    <div className="space-y-3">
      {preview && (
        <div className="relative w-full h-40 rounded-xl overflow-hidden bg-gray-100 border border-gray-200">
          <img src={preview} alt="Vorschau" className="w-full h-full object-cover" />
          <button
            type="button"
            onClick={() => { setPreview(''); onChange(''); }}
            className="absolute top-2 right-2 bg-[rgb(var(--theme-600))] text-white p-1 rounded-full hover:opacity-90"
          >
            <X size={14} />
          </button>
        </div>
      )}
      <label className={`flex items-center justify-center gap-2 w-full p-4 rounded-xl border-2 border-dashed cursor-pointer transition-colors ${
        uploading ? 'border-gray-300 bg-gray-50 cursor-wait' : 'border-gray-300 bg-gray-50 hover:border-[rgb(var(--theme-400))] hover:bg-[rgb(var(--theme-50))]'
      }`}>
        {uploading ? (
          <><Loader2 size={20} className="animate-spin text-[rgb(var(--theme-600))]" /> <span className="text-sm font-bold text-gray-500">Wird hochgeladen...</span></>
        ) : (
          <><Upload size={20} className="text-gray-400" /> <span className="text-sm font-bold text-gray-500">{preview ? 'Bild ändern' : 'Bild hochladen'}</span></>
        )}
        <input
          type="file"
          accept="image/jpeg,image/png,image/gif,image/webp"
          className="hidden"
          onChange={handleFileUpload}
          disabled={uploading}
        />
      </label>
      {required && !preview && <input type="text" required value="" onChange={() => {}} className="opacity-0 h-0 absolute" tabIndex={-1} />}
    </div>
  );
};

// A simple Modal for adding/editing items
const AdminForm = ({ isOpen, onClose, onSubmit, fields, initialData, title, supabaseClient }) => {
  const [formData, setFormData] = useState(initialData || {});
  
  useEffect(() => {
    setFormData(initialData || {});
  }, [initialData, isOpen]);

  if (!isOpen) return null;

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-[100] flex items-center justify-center p-4">
      <div className="bg-white rounded-3xl p-8 max-w-md w-full relative max-h-[90vh] overflow-y-auto">
        <button onClick={onClose} className="absolute top-6 right-6 text-gray-400 hover:text-amber-200">
          <X size={24} />
        </button>
        <h3 className="text-2xl font-black uppercase italic mb-6">{title}</h3>
        <form onSubmit={handleSubmit} className="space-y-4">
          {fields.map(field => (
            <div key={field.name}>
              <label className="block text-xs font-bold text-gray-500 uppercase mb-1">{field.label}</label>
              {field.type === 'image' ? (
                <ImageUploadField
                  value={formData[field.name] || ''}
                  onChange={(url) => setFormData({...formData, [field.name]: url})}
                  required={field.required}
                  supabaseClient={supabaseClient}
                />
              ) : field.type === 'images' ? (
                <MultiImageUploadField
                  value={formData[field.name] || ''}
                  onChange={(val) => setFormData({...formData, [field.name]: val})}
                  supabaseClient={supabaseClient}
                />
              ) : field.type === 'textarea' ? (                <textarea
                  className="w-full bg-gray-100 p-3 rounded-xl focus:ring-2 focus:ring-[rgb(var(--theme-600))] outline-none"
                  value={formData[field.name] || ''}
                  onChange={(e) => setFormData({...formData, [field.name]: e.target.value})}
                  required={field.required}
                  rows={4}
                />
              ) : field.type === 'select' ? (
                <select
                  className="w-full bg-gray-100 p-3 rounded-xl focus:ring-2 focus:ring-[rgb(var(--theme-600))] outline-none"
                  value={formData[field.name] || ''}
                  onChange={(e) => setFormData({...formData, [field.name]: e.target.value})}
                  required={field.required}
                >
                  <option value="">-- Bitte wählen --</option>
                  {field.options.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                </select>
              ) : (
                <input
                  type={field.type || 'text'}
                  className="w-full bg-gray-100 p-3 rounded-xl focus:ring-2 focus:ring-[rgb(var(--theme-600))] outline-none"
                  value={formData[field.name] || ''}
                  onChange={(e) => setFormData({...formData, [field.name]: e.target.value})}
                  required={field.required}
                />
              )}
            </div>
          ))}
          <button type="submit" className="w-full bg-[rgb(var(--theme-600))] text-white font-bold uppercase py-4 rounded-xl hover:opacity-90 transition-colors mt-6">
            Speichern
          </button>
        </form>
      </div>
    </div>
  );
};

const App = () => {
  const [activeTab, setActiveTab] = useState('home');
  const [isAdmin, setIsAdmin] = useState(false);
  const [supabase, setSupabase] = useState(null);
  const [isLoginLoading, setIsLoginLoading] = useState(false);
  const [loginError, setLoginError] = useState(null);
  
  // Data States
  const [trainings, setTrainings] = useState([]);
  const [successes, setSuccesses] = useState([]);
  const [events, setEvents] = useState([]);
  const [trainers, setTrainers] = useState([]);
  const [features, setFeatures] = useState([]);
  const [pages, setPages] = useState({});
  const [isScriptLoaded, setIsScriptLoaded] = useState(false);
  const [trainingFilter, setTrainingFilter] = useState('Alle');
  const [showTrainingFilter, setShowTrainingFilter] = useState(false);

  // Modal States
  const [modalConfig, setModalConfig] = useState({ isOpen: false });
  const [lightbox, setLightbox] = useState({ isOpen: false, images: [], index: 0 });

  const closeLightbox = useCallback(() => setLightbox(prev => ({ ...prev, isOpen: false })), []);
  const prevImage = useCallback(() => setLightbox(prev => ({ ...prev, index: (prev.index - 1 + prev.images.length) % prev.images.length })), []);
  const nextImage = useCallback(() => setLightbox(prev => ({ ...prev, index: (prev.index + 1) % prev.images.length })), []);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!lightbox.isOpen) return;
      if (e.key === 'ArrowLeft') prevImage();
      if (e.key === 'ArrowRight') nextImage();
      if (e.key === 'Escape') closeLightbox();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [lightbox.isOpen, prevImage, nextImage, closeLightbox]);
  // Theme State
  const [currentTheme, setCurrentTheme] = useState(() => {
    const saved = localStorage.getItem('tus-theme') || 'klassisch-rot';
    applyTheme(saved);
    return saved;
  });
  const [showThemePicker, setShowThemePicker] = useState(false);

  useEffect(() => {
    applyTheme(currentTheme);
  }, [currentTheme]);

  const handleThemeChange = async (themeKey) => {
    setCurrentTheme(themeKey);
    localStorage.setItem('tus-theme', themeKey);
    applyTheme(themeKey);
    // Save to Supabase if available
    if (supabase) {
      const data = { slug: 'theme', title: themeKey, content: '', subtitle: '', image_url: '' };
      const { data: existing } = await supabase.from('pages').select('id').eq('slug', 'theme').maybeSingle();
      if (existing) {
        await supabase.from('pages').update(data).eq('id', existing.id);
      } else {
        await supabase.from('pages').insert([data]);
      }
    }
    setShowThemePicker(false);
  };

  // Load Supabase Script Dynamically
  useEffect(() => {
    if (window.supabase) {
      const client = window.supabase.createClient(supabaseUrl, supabaseAnonKey);
      setSupabase(client);
      setIsScriptLoaded(true);
      return;
    }
    const script = document.createElement('script');
    script.src = 'https://cdn.jsdelivr.net/npm/@supabase/supabase-js@2';
    script.async = true;
    script.onload = () => {
      const client = window.supabase.createClient(supabaseUrl, supabaseAnonKey);
      setSupabase(client);
      setIsScriptLoaded(true);
    };
    script.onerror = () => console.error('Failed to load Supabase.');
    document.head.appendChild(script);
  }, []);

  const fetchData = useCallback(async (client) => {
    if (!client) return;
    try {
      const [tData, sData, eData, trData, fData] = await Promise.all([
        client.from('trainings').select('*'), // Sort locally
        client.from('successes').select('*').order('year', { ascending: false }),
        client.from('events').select('*').order('date', { ascending: true }),
        client.from('trainers').select('*'),
        client.from('features').select('*').order('sort_order', { ascending: true })
      ]);
      if (tData.data) setTrainings(tData.data);
      if (sData.data) setSuccesses(sData.data);
      if (eData.data) setEvents(eData.data);
      if (trData.data) setTrainers(trData.data);
      if (fData && fData.data) setFeatures(fData.data);
      
      const pData = await client.from('pages').select('*');
      if (pData && pData.data) {
        const pObj = {};
        pData.data.forEach(p => pObj[p.slug] = p);
        setPages(pObj);
      }
    } catch (err) {
      console.error(err);
    }
  }, []);

  useEffect(() => {
    if (supabase && isScriptLoaded) {
      const checkUser = async () => {
        const { data: { user } } = await supabase.auth.getUser();
        if (user) { setIsAdmin(true); }
      };
      checkUser();
      fetchData(supabase);
    }
  }, [supabase, isScriptLoaded, fetchData]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoginLoading(true);
    const { error } = await supabase.auth.signInWithPassword({
      email: e.target.email.value,
      password: e.target.password.value
    });
    if (error) setLoginError(error.message);
    else { setIsAdmin(true); setActiveTab('home'); }
    setIsLoginLoading(false);
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    setIsAdmin(false); setActiveTab('home');
  };

  const handleDelete = async (table, id) => {
    if (window.confirm('Wirklich löschen?')) {
      await supabase.from(table).delete().eq('id', id);
      fetchData(supabase);
    }
  };

  const handleSave = async (table, data) => {
    let res;
    if (table === 'pages') {
      const { data: existing } = await supabase.from('pages').select('id').eq('slug', data.slug).maybeSingle();
      if (existing) {
        res = await supabase.from('pages').update(data).eq('id', existing.id);
      } else {
        res = await supabase.from('pages').insert([data]);
      }
    } else {
      if (data.id) {
        res = await supabase.from(table).update(data).eq('id', data.id);
      } else {
        res = await supabase.from(table).insert([data]);
      }
    }

    if (res && res.error) {
       alert('Fehler beim Speichern: ' + res.error.message + '\n\nHinweis: Falls dies die Seite betrifft, stelle sicher, dass die Tabelle "pages" in Supabase mit den Spalten (id, slug, title, subtitle, content, image_url) existiert.');
    } else {
       fetchData(supabase);
       setModalConfig({ isOpen: false });
    }
  };

  const openModal = (table, fields, title, initialData = null) => {
    setModalConfig({
      isOpen: true,
      table,
      fields,
      title,
      initialData,
      onSubmit: (data) => handleSave(table, data)
    });
  };

  // --- COMPONENTS ---

  const Navigation = () => {
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    
    return (
      <nav className="bg-[rgb(var(--theme-900))] shadow-lg sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 flex justify-between h-20 items-center">
          <div className="flex items-center space-x-3 cursor-pointer" onClick={() => setActiveTab('home')}>
            <div className="w-10 h-10 bg-[rgb(var(--theme-600))] rounded-full flex items-center justify-center text-white font-black italic">TuS</div>
            <h1 className="text-xl font-black uppercase text-white">TuS International</h1>
          </div>
          
          {/* Desktop Menu */}
          <div className="hidden lg:flex items-center space-x-4 flex-wrap justify-end">
            {['Home', 'Karate', 'Trainingsplan', 'Trainer', 'Erfolge', 'Termine', 'Privattraining', 'Selbstverteidigungskurs', 'Kontakt'].map((item) => (
              <button key={item} onClick={() => setActiveTab(item.toLowerCase().replace(' ', '-'))} className={`text-sm font-bold uppercase hover:text-amber-200 transition-colors ${activeTab === item.toLowerCase().replace(' ', '-') ? 'text-amber-200' : 'text-white/80'}`}>
                {item}
              </button>
            ))}
            {isAdmin ? <button onClick={handleLogout} className="text-amber-200 ml-2"><LogOut size={20}/></button> : <button onClick={() => setActiveTab('login')} className="ml-2 text-white/80"><LogIn size={20}/></button>}
          </div>

          {/* Mobile Menu Toggle */}
          <div className="lg:hidden flex items-center">
            <button onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} className="text-white">
              {isMobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="lg:hidden bg-[rgb(var(--theme-800))] border-t border-[rgb(var(--theme-700))] px-4 py-4 space-y-4">
            {['Home', 'Karate', 'Trainingsplan', 'Trainer', 'Erfolge', 'Termine', 'Privattraining', 'Selbstverteidigungskurs', 'Kontakt'].map((item) => (
              <button key={item} onClick={() => { setActiveTab(item.toLowerCase().replace(' ', '-')); setIsMobileMenuOpen(false); }} className={`block w-full text-left text-base font-bold uppercase py-2 hover:text-amber-200 transition-colors ${activeTab === item.toLowerCase().replace(' ', '-') ? 'text-amber-200' : 'text-white/80'}`}>
                {item}
              </button>
            ))}
            {isAdmin ? (
              <button onClick={() => { handleLogout(); setIsMobileMenuOpen(false); }} className="block w-full text-left text-sm font-bold uppercase py-2 text-amber-200">Logout</button>
            ) : (
              <button onClick={() => { setActiveTab('login'); setIsMobileMenuOpen(false); }} className="block w-full text-left text-sm font-bold uppercase py-2 text-white/80">Admin Login</button>
            )}
          </div>
        )}
      </nav>
    );
  };

  const TrainingSection = () => {
    const locations = ["Alle", ...new Set(trainings.map(t => t.location).filter(Boolean))];
    const dayNames = ['Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag', 'Sonntag'];
    const dayOrder = {};
    dayNames.forEach((d, i) => { dayOrder[d.toLowerCase()] = i + 1; });

    const getDayOrder = (day) => {
      if (!day) return 99;
      const lower = day.toLowerCase();
      if (dayOrder[lower]) return dayOrder[lower];
      const match = dayNames.find(d => d.toLowerCase().startsWith(lower));
      return match ? dayOrder[match.toLowerCase()] : 99;
    };

    const normalizeTime = (time) => {
      if (!time) return '';
      return time.replace(/\./g, ':').replace(/\s/g, '');
    };
    
    const filtered = (trainingFilter === "Alle" ? trainings : trainings.filter(t => t.location === trainingFilter))
      .sort((a, b) => {
        const dayA = getDayOrder(a.day);
        const dayB = getDayOrder(b.day);
        if (dayA !== dayB) return dayA - dayB;
        return normalizeTime(a.time).localeCompare(normalizeTime(b.time));
      });

    const fields = [
      { name: 'day', label: 'Tag', type: 'select', options: dayNames, required: true },
      { name: 'topic', label: 'Zielgruppe', required: true },
      { name: 'time', label: 'Zeit (z.B. 18:00 - 19:30)', required: true },
      { name: 'location', label: 'Ort', required: true }
    ];

    return (
      <div className="max-w-6xl mx-auto px-4 py-24">
        <div className="flex justify-between items-center mb-12">
          <h2 className="text-4xl md:text-5xl font-black uppercase italic">Trainings<span className="text-[rgb(var(--theme-600))]">plan</span></h2>
          <div className="flex items-center gap-3">
            <button 
              onClick={() => { setShowTrainingFilter(!showTrainingFilter); if (showTrainingFilter) setTrainingFilter('Alle'); }}
              className={`p-3 rounded-full shadow-lg transition-all ${showTrainingFilter ? 'bg-[rgb(var(--theme-600))] text-white' : 'bg-[rgb(var(--theme-50))] text-gray-500 hover:bg-[rgb(var(--theme-100))]'}`}
              title={showTrainingFilter ? 'Filter ausblenden' : 'Nach Ort filtern'}
            >
              <Filter size={20}/>
            </button>
            {isAdmin && (
              <button onClick={() => openModal('trainings', fields, 'Training Hinzufügen')} className="bg-[rgb(var(--theme-600))] text-white p-3 rounded-full hover:opacity-90 shadow-lg">
                <Plus size={24}/>
              </button>
            )}
          </div>
        </div>
        {showTrainingFilter && (
          <div className="flex justify-center gap-2 mb-10 flex-wrap animate-fade-in">
            {locations.map(loc => (
              <button key={loc} onClick={() => setTrainingFilter(loc)} className={`px-6 py-2 rounded-full text-xs font-bold uppercase transition-all ${trainingFilter === loc ? 'bg-[rgb(var(--theme-600))] text-white shadow-md' : 'bg-[rgb(var(--theme-50))] text-gray-500 hover:bg-[rgb(var(--theme-100))]'}`}>{loc}</button>
            ))}
          </div>
        )}
        <div className="bg-[rgb(var(--theme-50))] rounded-[2rem] shadow-xl overflow-hidden border border-[rgb(var(--theme-100))]">
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse min-w-[600px]">
              <thead className="bg-[rgb(var(--theme-900))] text-white text-xs uppercase tracking-widest">
                <tr>
                  <th className="p-6">Tag</th>
                  <th className="p-6">Zielgruppe</th>
                  <th className="p-6">Zeit</th>
                  <th className="p-6">Ort</th>
                  {isAdmin && <th className="p-6 text-right">Aktion</th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50 font-bold">
                {filtered.map((t) => (
                  <tr key={t.id} className="hover:bg-[rgb(var(--theme-50))]/50 transition-colors group">
                    <td className="p-6 uppercase italic text-lg">{t.day}</td>
                    <td className="p-6 text-gray-900">{t.topic}</td>
                    <td className="p-6 text-gray-900 font-mono text-xl font-black">{t.time}</td>
                    <td className="p-6 text-sm italic">
                      <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(t.location)}`} target="_blank" rel="noopener noreferrer" className="text-[rgb(var(--theme-700))] underline decoration-[rgb(var(--theme-400))] hover:opacity-90 transition-colors">
                        {t.location}
                      </a>
                    </td>
                    {isAdmin && (
                      <td className="p-6 text-right flex justify-end gap-3 opacity-100 lg:opacity-0 lg:group-hover:opacity-100 transition-opacity">
                        <button onClick={() => openModal('trainings', fields, 'Training Bearbeiten', t)} className="p-2 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100"><Edit2 size={18}/></button>
                        <button onClick={() => handleDelete('trainings', t.id)} className="p-2 bg-[rgb(var(--theme-50))] text-[rgb(var(--theme-600))] rounded-lg hover:bg-[rgb(var(--theme-100))]"><Trash2 size={18}/></button>
                      </td>
                    )}
                  </tr>
                ))}
                {filtered.length === 0 && (
                  <tr>
                    <td colSpan={isAdmin ? 5 : 4} className="p-6 text-center text-gray-500 italic font-normal">Keine Trainings gefunden.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  const ErfolgeSection = () => {
    const years = [...new Set(successes.map(s => s.year))].sort((a,b) => b-a);
    const fields = [
      { name: 'year', label: 'Jahr', type: 'number', required: true },
      { name: 'title', label: 'Titel', required: true },
      { name: 'description', label: 'Beschreibung', type: 'textarea' },
      { name: 'image_url', label: 'Bilder', type: 'images' }
    ];

    const renderImages = (imageUrl) => {
      if (!imageUrl) return null;
      let images = [];
      try {
        if (imageUrl.startsWith('[') && imageUrl.endsWith(']')) {
          images = JSON.parse(imageUrl);
        } else {
          images = [imageUrl];
        }
      } catch (e) {
        images = [imageUrl];
      }

      if (images.length === 0) return null;
      if (images.length === 1) {
        return (
          <div className="h-64 md:h-80 rounded-2xl overflow-hidden mb-4 bg-gray-100 cursor-pointer shadow-inner" onClick={() => setLightbox({ isOpen: true, images, index: 0 })}>
            <img src={images[0]} alt="" className="w-full h-full object-contain hover:scale-105 transition-transform duration-500"/>
          </div>
        );
      }

      const displayImages = images.slice(0, 3);
      const remainingCount = images.length - 3;

      return (
        <div className="grid grid-cols-2 gap-2 mb-4">
          {displayImages.map((img, i) => {
            const isLastVisible = i === 2;
            const hasMore = remainingCount > 0;
            
            return (
              <div 
                key={i} 
                className={`rounded-xl overflow-hidden bg-gray-100 cursor-pointer shadow-sm relative group ${i === 0 ? 'col-span-2 h-48 md:h-64' : 'h-32 md:h-40'}`} 
                onClick={() => setLightbox({ isOpen: true, images, index: i })}
              >
                <img src={img} alt="" className="w-full h-full object-contain hover:scale-110 transition-transform duration-500"/>
                {isLastVisible && hasMore && (
                  <div className="absolute inset-0 bg-black/60 flex items-center justify-center backdrop-blur-[2px] group-hover:bg-black/50 transition-colors">
                    <span className="text-white font-black text-xl">+{remainingCount}</span>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      );
    };

    return (
      <div className="max-w-6xl mx-auto px-4 py-24">
        <div className="flex justify-between items-center mb-16">
          <h2 className="text-4xl md:text-5xl font-black uppercase italic">Unsere <span className="text-[rgb(var(--theme-600))]">Erfolge</span></h2>
          {isAdmin && (
            <button onClick={() => openModal('successes', fields, 'Erfolg Hinzufügen')} className="bg-[rgb(var(--theme-600))] text-white p-3 rounded-full hover:opacity-90 shadow-lg">
              <Plus size={24}/>
            </button>
          )}
        </div>
        {years.length === 0 && <p className="text-center text-gray-500 italic">Keine Erfolge vorhanden.</p>}
        {years.map(year => (
          <div key={year} className="mb-20">
            <div className="flex items-center mb-8">
              <span className="text-5xl font-black text-[rgb(var(--theme-600))]/20 mr-4">{year}</span>
              <div className="h-px flex-grow bg-gray-200"></div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-8">
              {successes.filter(s => s.year === year).map((s) => (
                <div key={s.id} className="bg-[rgb(var(--theme-50))] p-6 rounded-3xl shadow-lg border border-[rgb(var(--theme-100))] relative group transition-transform hover:-translate-y-1">
                  {isAdmin && (
                    <div className="absolute top-4 right-4 z-10 flex gap-2 opacity-100 lg:opacity-0 lg:group-hover:opacity-100 transition-opacity">
                       <button onClick={() => openModal('successes', fields, 'Erfolg Bearbeiten', s)} className="bg-white p-2 rounded-full shadow-md text-blue-600 hover:bg-blue-50"><Edit2 size={16}/></button>
                       <button onClick={() => handleDelete('successes', s.id)} className="bg-white p-2 rounded-full shadow-md text-[rgb(var(--theme-600))] hover:bg-blue-50"><Trash2 size={16}/></button>
                    </div>
                  )}
                  {renderImages(s.image_url)}
                  <h4 className="font-black uppercase text-lg">{s.title}</h4>
                  <p className="text-sm text-gray-900 italic mt-2 whitespace-pre-line">{s.description}</p>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  };

  const handleTogglePin = async (trainer) => {
    const newPinned = !trainer.pinned;
    await supabase.from('trainers').update({ pinned: newPinned }).eq('id', trainer.id);
    fetchData(supabase);
  };

  const [draggedTrainer, setDraggedTrainer] = useState(null);
  const [dragOverTrainer, setDragOverTrainer] = useState(null);

  const handleTrainerDragStart = (e, trainer) => {
    setDraggedTrainer(trainer);
    e.dataTransfer.effectAllowed = 'move';
    e.currentTarget.style.opacity = '0.5';
  };

  const handleTrainerDragEnd = (e) => {
    e.currentTarget.style.opacity = '1';
    setDraggedTrainer(null);
    setDragOverTrainer(null);
  };

  const handleTrainerDragOver = (e, trainer) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (draggedTrainer && trainer.id !== draggedTrainer.id) {
      setDragOverTrainer(trainer);
    }
  };

  const handleTrainerDrop = async (e, targetTrainer) => {
    e.preventDefault();
    if (!draggedTrainer || draggedTrainer.id === targetTrainer.id) return;

    const sorted = [...trainers].sort((a, b) => {
      if (a.pinned && !b.pinned) return -1;
      if (!a.pinned && b.pinned) return 1;
      return (a.sort_order || 0) - (b.sort_order || 0);
    });

    const fromIndex = sorted.findIndex(t => t.id === draggedTrainer.id);
    const toIndex = sorted.findIndex(t => t.id === targetTrainer.id);
    const reordered = [...sorted];
    const [moved] = reordered.splice(fromIndex, 1);
    reordered.splice(toIndex, 0, moved);

    // Update sort_order for all trainers
    const updates = reordered.map((t, i) => ({ ...t, sort_order: i }));
    setTrainers(updates); // optimistic update

    await Promise.all(
      updates.map(t => supabase.from('trainers').update({ sort_order: t.sort_order }).eq('id', t.id))
    );

    setDraggedTrainer(null);
    setDragOverTrainer(null);
  };

  const TrainerSection = () => {
    const fields = [
      { name: 'name', label: 'Name', required: true },
      { name: 'role', label: 'Rolle/Titel', required: true },
      { name: 'bio', label: 'Biografie', type: 'textarea' },
      { name: 'image_url', label: 'Bild (optional)', type: 'image' }
    ];

    const sortedTrainers = [...trainers].sort((a, b) => {
      if (a.pinned && !b.pinned) return -1;
      if (!a.pinned && b.pinned) return 1;
      return (a.sort_order || 0) - (b.sort_order || 0);
    });

    return (
      <div className="max-w-6xl mx-auto px-4 py-24">
         <div className="flex justify-between items-center mb-16">
          <h2 className="text-4xl md:text-5xl font-black uppercase italic">Unsere <span className="text-[rgb(var(--theme-600))]">Trainer</span></h2>
          {isAdmin && (
            <button onClick={() => openModal('trainers', fields, 'Trainer Hinzufügen')} className="bg-[rgb(var(--theme-600))] text-white p-3 rounded-full hover:opacity-90 shadow-lg">
              <Plus size={24}/>
            </button>
          )}
        </div>
        {trainers.length === 0 && <p className="text-center text-gray-500 italic">Keine Trainer vorhanden.</p>}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
           {sortedTrainers.map(t => (
             <div 
               key={t.id} 
               draggable={isAdmin}
               onDragStart={isAdmin ? (e) => handleTrainerDragStart(e, t) : undefined}
               onDragEnd={isAdmin ? handleTrainerDragEnd : undefined}
               onDragOver={isAdmin ? (e) => handleTrainerDragOver(e, t) : undefined}
               onDrop={isAdmin ? (e) => handleTrainerDrop(e, t) : undefined}
               className={`bg-[rgb(var(--theme-50))] p-8 rounded-3xl shadow-lg border text-center relative group transition-all hover:-translate-y-1 ${t.pinned ? 'border-[rgb(var(--theme-400))] ring-2 ring-[rgb(var(--theme-200))]' : 'border-[rgb(var(--theme-100))]'} ${isAdmin ? 'cursor-grab active:cursor-grabbing' : ''} ${dragOverTrainer?.id === t.id ? 'ring-2 ring-blue-400 scale-[1.02]' : ''}`}
             >
                {t.pinned && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[rgb(var(--theme-600))] text-white text-xs font-bold px-3 py-1 rounded-full flex items-center gap-1 shadow-md">
                    <Pin size={12} /> Angepinnt
                  </div>
                )}
                {isAdmin && (
                    <div className="absolute top-4 right-4 z-10 flex gap-2 opacity-100 lg:opacity-0 lg:group-hover:opacity-100 transition-opacity">
                       <button onClick={() => handleTogglePin(t)} className={`p-2 rounded-full shadow-md transition-colors ${t.pinned ? 'bg-[rgb(var(--theme-600))] text-white hover:opacity-90' : 'bg-white text-gray-400 hover:bg-gray-50'}`} title={t.pinned ? 'Unpin' : 'Pin'}><Pin size={16}/></button>
                       <button onClick={() => openModal('trainers', fields, 'Trainer Bearbeiten', t)} className="bg-white p-2 rounded-full shadow-md text-blue-600 hover:bg-blue-50"><Edit2 size={16}/></button>
                       <button onClick={() => handleDelete('trainers', t.id)} className="bg-white p-2 rounded-full shadow-md text-[rgb(var(--theme-600))] hover:bg-blue-50"><Trash2 size={16}/></button>
                    </div>
                )}
                {isAdmin && (
                  <div className="absolute top-4 left-4 z-10 opacity-100 lg:opacity-0 lg:group-hover:opacity-100 transition-opacity text-gray-400 cursor-grab active:cursor-grabbing">
                    <GripVertical size={20}/>
                  </div>
                )}
                <div className="w-40 h-40 mx-auto rounded-full overflow-hidden mb-6 border-4 border-gray-50 shadow-inner bg-gray-100 cursor-pointer" onClick={() => setLightbox({ isOpen: true, images: [t.image_url || 'https://images.unsplash.com/photo-1544367567-0f2fcb009e0b?auto=format&fit=crop&q=80'], index: 0 })}>
                  <img src={t.image_url || 'https://images.unsplash.com/photo-1544367567-0f2fcb009e0b?auto=format&fit=crop&q=80'} alt={t.name} className="w-full h-full object-cover" />
                </div>
                <h4 className="font-black uppercase text-2xl mb-1">{t.name}</h4>
                <p className="text-gray-900 font-bold italic mb-4">{t.role}</p>
                <p className="text-gray-900 text-sm leading-relaxed">{t.bio}</p>
             </div>
           ))}
        </div>
      </div>
    );
  };

  const EventsSection = () => {
    const fields = [
      { name: 'name', label: 'Event Name', required: true },
      { name: 'date', label: 'Datum', type: 'date', required: true },
      { name: 'location', label: 'Ort', required: true }
    ];

    return (
      <div className="max-w-4xl mx-auto px-4 py-24">
         <div className="flex justify-between items-center mb-16">
          <h2 className="text-4xl md:text-5xl font-black uppercase italic">Aktuelle <span className="text-[rgb(var(--theme-600))]">Termine</span></h2>
          {isAdmin && (
            <button onClick={() => openModal('events', fields, 'Termin Hinzufügen')} className="bg-[rgb(var(--theme-600))] text-white p-3 rounded-full hover:opacity-90 shadow-lg">
              <Plus size={24}/>
            </button>
          )}
        </div>
        <div className="space-y-4">
           {events.map(e => (
             <div key={e.id} className="bg-[rgb(var(--theme-50))] p-6 rounded-2xl shadow-md border border-[rgb(var(--theme-100))] flex flex-col md:flex-row items-start md:items-center justify-between group gap-4 transition-all hover:shadow-lg">
                <div className="flex flex-row items-center gap-6 w-full">
                   <div className="bg-[rgb(var(--theme-50))] text-gray-900 p-4 rounded-xl text-center min-w-[100px] shrink-0">
                      <div className="text-sm font-bold uppercase">{new Date(e.date + 'T00:00:00').toLocaleDateString('de-DE', { month: 'short' })}</div>
                      <div className="text-3xl font-black">{new Date(e.date + 'T00:00:00').getDate()}</div>
                   </div>
                   <div className="flex-grow">
                      <h4 className="font-black uppercase text-xl md:text-2xl">{e.name}</h4>
                      <p className="text-gray-900 flex items-center gap-2 mt-2 text-sm md:text-base"><MapPin size={16} className="text-gray-900"/> {e.location}</p>
                   </div>
                </div>
                {isAdmin && (
                    <div className="flex gap-2 opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity self-end md:self-auto shrink-0">
                       <button onClick={() => openModal('events', fields, 'Termin Bearbeiten', e)} className="p-3 text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-xl"><Edit2 size={20}/></button>
                       <button onClick={() => handleDelete('events', e.id)} className="p-3 text-[rgb(var(--theme-600))] bg-[rgb(var(--theme-50))] hover:bg-red-100 rounded-xl"><Trash2 size={20}/></button>
                    </div>
                )}
             </div>
           ))}
           {events.length === 0 && <p className="text-gray-500 italic text-center py-12 bg-[rgb(var(--theme-50))] rounded-2xl shadow-sm border border-[rgb(var(--theme-100))]">Keine Termine vorhanden.</p>}
        </div>
      </div>
    );
  };

  // --- FEATURE CARD HELPERS ---
  const featureFields = [
    { name: 'title', label: 'Titel', required: true },
    { name: 'description', label: 'Beschreibung', required: true },
    { name: 'icon', label: 'Icon', type: 'select', options: iconNames, required: true },
    { name: 'sort_order', label: 'Reihenfolge (0, 1, 2...)', type: 'number' }
  ];

  const defaultFeatures = {
    'privattraining': [
      { icon: 'UserCheck', title: '100% individuelle Betreuung', description: 'Maximale Aufmerksamkeit für Ihre persönliche Entwicklung' },
      { icon: 'Clock', title: 'Flexible Trainingszeiten', description: 'Termine angepasst an Ihren Alltag und Zeitplan' },
      { icon: 'Target', title: 'Persönlicher Trainingsplan', description: 'Maßgeschneidert für Ihre individuellen Ziele' },
      { icon: 'Star', title: 'Schnellere Fortschritte', description: 'Gezielte Korrekturen für optimale Ergebnisse' }
    ],
    'selbstverteidigungskurs': [
      { icon: 'Shield', title: 'Gefahren erkennen', description: 'Erkennen und Vermeiden von echten Gefahrensituationen' },
      { icon: 'Swords', title: 'Effektive Techniken', description: 'Verteidigungstechniken für den Alltag auf der Straße' },
      { icon: 'Star', title: 'Selbstbewusstsein', description: 'Stärkung des Selbstbewusstseins für sicheres Auftreten' },
      { icon: 'Target', title: 'Mentale Stärke', description: 'Vorbereitung auf Stress- und Konfliktsituationen' }
    ]
  };

  const getSectionFeatures = (section) => {
    const dbFeatures = features.filter(f => f.section === section);
    return dbFeatures.length > 0 ? dbFeatures : defaultFeatures[section] || [];
  };

  // Migrate default features to DB so they become editable/deletable
  const migrateDefaultsToDb = async (section) => {
    const defaults = defaultFeatures[section] || [];
    const toInsert = defaults.map((d, i) => ({
      section,
      title: d.title,
      description: d.description,
      icon: d.icon || 'Star',
      sort_order: i
    }));
    await supabase.from('features').insert(toInsert);
    const { data: newFeatures } = await supabase.from('features').select('*').eq('section', section).order('sort_order', { ascending: true });
    if (newFeatures) {
      setFeatures(prev => [...prev.filter(f => f.section !== section), ...newFeatures]);
    }
    return newFeatures || [];
  };

  const handleFeatureEdit = async (section, feature, index) => {
    if (feature.id) {
      openModal('features', featureFields, 'Vorteil Bearbeiten', feature);
    } else {
      // Migrate all defaults to DB first, then open editor for the specific one
      const saved = await migrateDefaultsToDb(section);
      const match = saved[index];
      if (match) {
        openModal('features', featureFields, 'Vorteil Bearbeiten', match);
      }
    }
  };

  const handleFeatureDelete = async (section, feature, index) => {
    if (feature.id) {
      handleDelete('features', feature.id);
    } else {
      if (window.confirm('Wirklich löschen?')) {
        // Migrate all defaults EXCEPT the deleted one to DB
        const defaults = defaultFeatures[section] || [];
        const remaining = defaults.filter((_, i) => i !== index);
        const toInsert = remaining.map((d, i) => ({
          section,
          title: d.title,
          description: d.description,
          icon: d.icon || 'Star',
          sort_order: i
        }));
        if (toInsert.length > 0) {
          await supabase.from('features').insert(toInsert);
        }
        fetchData(supabase);
      }
    }
  };

  // --- PRIVATE LEKTIONEN SECTION ---
  const PrivattrainingSection = () => {
    const page = pages['privattraining'] || defaultPages['privattraining'];
    const pageFields = [
      { name: 'title', label: 'Titel', required: true },
      { name: 'subtitle', label: 'Untertitel', type: 'textarea' },
      { name: 'content', label: 'Beschreibung', type: 'textarea', required: true },
      { name: 'image_url', label: 'Bild (optional)', type: 'image' }
    ];

    const sectionFeatures = getSectionFeatures('privattraining');

    const titleWords = (page.title || 'Privattraining').split(' ');
    const firstWord = titleWords[0];
    const restWords = titleWords.slice(1).join(' ');

    // Parse contact info from kontakt page for phone number
    const kontaktPage = pages['kontakt'] || defaultPages['kontakt'];
    const kontaktLines = (kontaktPage.content || '').split('\n').map(l => l.trim()).filter(Boolean);
    const phoneNumber = kontaktLines.find(l => l.startsWith('+')) || '+49 123 456 7890';

    return (
      <div className="max-w-6xl mx-auto px-4 py-24 relative group">
        {isAdmin && (
          <button 
            onClick={() => openModal('pages', pageFields, 'Privattraining Bearbeiten', { slug: 'privattraining', ...page })} 
            className="absolute top-8 right-4 bg-blue-600 text-white p-3 rounded-full hover:bg-blue-700 shadow-lg opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity z-10"
          >
            <Edit2 size={20}/>
          </button>
        )}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-black uppercase italic mb-4">{firstWord} <span className="text-[rgb(var(--theme-600))]">{restWords}</span></h2>
          <p className="text-gray-900 text-lg max-w-2xl mx-auto">{page.subtitle || ''}</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
          {(() => {
            const heroBox = pages['privattraining-hero'] || defaultPages['privattraining-hero'];
            const heroBoxFields = [
              { name: 'title', label: 'Überschrift', required: true }
            ];
            return (
              <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-[2rem] p-8 md:p-10 text-white flex flex-col justify-center shadow-2xl relative group/hero">
                {isAdmin && (
                  <button onClick={() => openModal('pages', heroBoxFields, 'Hero-Box Bearbeiten', { slug: 'privattraining-hero', ...heroBox })} className="absolute top-4 right-4 bg-blue-600 text-white p-2 rounded-full hover:bg-blue-700 shadow-lg opacity-0 group-hover/hero:opacity-100 transition-opacity z-10"><Edit2 size={16}/></button>
                )}
                <UserCheck size={40} className="text-[rgb(var(--theme-500))] mb-6" />
                <h3 className="text-2xl md:text-3xl font-black uppercase italic mb-4">{heroBox.title}</h3>
                <p className="text-white/70 leading-relaxed">
                  {page.content || ''}
                </p>
              </div>
            );
          })()}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {sectionFeatures.map((b, i) => (
              <div key={b.id || i} className="bg-[rgb(var(--theme-50))] p-6 rounded-2xl shadow-lg border border-[rgb(var(--theme-100))] hover:-translate-y-1 transition-transform relative group/card">
                {isAdmin && (
                  <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover/card:opacity-100 transition-opacity">
                    <button onClick={() => handleFeatureEdit('privattraining', b, i)} className="bg-blue-600 text-white p-1 rounded-full hover:bg-blue-700"><Edit2 size={12}/></button>
                    <button onClick={() => handleFeatureDelete('privattraining', b, i)} className="bg-[rgb(var(--theme-600))] text-white p-1 rounded-full hover:opacity-90"><Trash2 size={12}/></button>
                  </div>
                )}
                <div className="w-12 h-12 bg-[rgb(var(--theme-50))] rounded-xl flex items-center justify-center text-[rgb(var(--theme-600))] mb-4">
                  {getIcon(b.icon, 24)}
                </div>
                <h4 className="font-black uppercase text-sm mb-1">{b.title}</h4>
                <p className="text-gray-900 text-xs leading-relaxed">{b.description}</p>
              </div>
            ))}
          </div>
        </div>

        {isAdmin && (
          <div className="flex justify-center mb-8">
            <button onClick={() => openModal('features', featureFields, 'Vorteil Hinzufügen', { section: 'privattraining', sort_order: sectionFeatures.length })} className="bg-[rgb(var(--theme-600))] text-white px-6 py-2 rounded-full hover:opacity-90 shadow-lg flex items-center gap-2 text-sm font-bold uppercase">
              <Plus size={16}/> Vorteil hinzufügen
            </button>
          </div>
        )}

        {page.image_url && (
          <div className="mb-12 flex justify-center">
            <img src={page.image_url} alt={page.title} className="rounded-3xl max-w-full h-auto object-cover shadow-2xl cursor-pointer" style={{maxHeight: '400px'}} onClick={() => setLightbox({ isOpen: true, images: [page.image_url], index: 0 })} />
          </div>
        )}

        {(() => {
          const cta = pages['privattraining-cta'] || defaultPages['privattraining-cta'];
          const ctaFields = [
            { name: 'title', label: 'Überschrift', required: true },
            { name: 'content', label: 'Info-Text (z.B. Preise auf Anfrage)', required: true },
            { name: 'subtitle', label: 'Button-Text', required: true }
          ];
          return (
            <div className="bg-[rgb(var(--theme-50))] rounded-3xl p-8 md:p-10 shadow-lg border border-[rgb(var(--theme-100))] text-center relative group/cta">
              {isAdmin && (
                <button onClick={() => openModal('pages', ctaFields, 'CTA Bearbeiten', { slug: 'privattraining-cta', ...cta })} className="absolute top-4 right-4 bg-blue-600 text-white p-2 rounded-full hover:bg-blue-700 shadow-lg opacity-0 group-hover/cta:opacity-100 transition-opacity z-10"><Edit2 size={16}/></button>
              )}
              <p className="text-gray-900 text-lg mb-2">{cta.content}</p>
              <h3 className="text-xl font-black uppercase italic mb-6">{cta.title}</h3>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button onClick={() => setActiveTab('kontakt')} className="bg-[rgb(var(--theme-600))] text-white px-8 py-3 rounded-xl font-bold uppercase hover:opacity-90 transition-colors shadow-lg shadow-red-600/20">
                  {cta.subtitle || 'Jetzt anfragen'}
                </button>
                <a href={`tel:${phoneNumber.replace(/\s/g, '')}`} className="bg-[rgb(var(--theme-800))] text-white px-8 py-3 rounded-xl font-bold uppercase hover:opacity-90 transition-colors flex items-center justify-center gap-2">
                  <Phone size={18} /> Anrufen
                </a>
              </div>
            </div>
          );
        })()}
      </div>
    );
  };

  // --- GRUPPENKURSE SECTION ---
  const SelbstverteidigungskursSection = () => {
    const page = pages['selbstverteidigungskurs'] || defaultPages['selbstverteidigungskurs'];
    const pageFields = [
      { name: 'title', label: 'Titel', required: true },
      { name: 'subtitle', label: 'Untertitel', type: 'textarea' },
      { name: 'content', label: 'Beschreibung', type: 'textarea', required: true },
      { name: 'image_url', label: 'Bild (optional)', type: 'image' }
    ];

    const sectionFeatures = getSectionFeatures('selbstverteidigungskurs');

    const titleWords = (page.title || 'Selbstverteidigungskurs').split(' ');
    const lastWord = titleWords[titleWords.length - 1];
    const firstWords = titleWords.slice(0, -1).join(' ');

    return (
      <div className="max-w-6xl mx-auto px-4 py-24 relative group">
        {isAdmin && (
          <button 
            onClick={() => openModal('pages', pageFields, 'Selbstverteidigungskurs Bearbeiten', { slug: 'selbstverteidigungskurs', ...page })} 
            className="absolute top-8 right-4 bg-blue-600 text-white p-3 rounded-full hover:bg-blue-700 shadow-lg opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity z-10"
          >
            <Edit2 size={20}/>
          </button>
        )}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-black uppercase italic mb-4">{firstWords} <span className="text-[rgb(var(--theme-600))]">{lastWord}</span></h2>
          <p className="text-gray-900 text-lg max-w-2xl mx-auto">{page.subtitle || ''}</p>
        </div>

        {(() => {
          const heroBox = pages['selbstverteidigungskurs-hero'] || defaultPages['selbstverteidigungskurs-hero'];
          const heroBoxFields = [
            { name: 'title', label: 'Überschrift', required: true }
          ];
          return (
            <div className="bg-gradient-to-br from-blue-600 to-blue-700 rounded-[2rem] p-8 md:p-12 mb-12 text-white shadow-2xl shadow-blue-500/30 relative group/hero">
              {isAdmin && (
                <button onClick={() => openModal('pages', heroBoxFields, 'Hero-Box Bearbeiten', { slug: 'selbstverteidigungskurs-hero', ...heroBox })} className="absolute top-4 right-4 bg-blue-600 text-white p-2 rounded-full hover:bg-blue-700 shadow-lg opacity-0 group-hover/hero:opacity-100 transition-opacity z-10"><Edit2 size={16}/></button>
              )}
              <div className="max-w-3xl mx-auto text-center">
                <Shield size={48} className="mx-auto mb-6 text-white/90" />
                <h3 className="text-2xl md:text-3xl font-black uppercase italic mb-4">{heroBox.title}</h3>
                <p className="text-white/80 leading-relaxed text-lg">
                  {page.content || ''}
                </p>
              </div>
            </div>
          );
        })()}

        {page.image_url && (
          <div className="mb-12 flex justify-center">
            <img src={page.image_url} alt={page.title} className="rounded-3xl max-w-full h-auto object-cover shadow-2xl cursor-pointer" style={{maxHeight: '400px'}} onClick={() => setLightbox({ isOpen: true, images: [page.image_url], index: 0 })} />
          </div>
        )}

        <div className="flex justify-between items-center mb-6">
          <h3 className="text-xl font-black uppercase italic">Themen</h3>
          {isAdmin && (
            <button onClick={() => openModal('features', featureFields, 'Thema Hinzufügen', { section: 'selbstverteidigungskurs', sort_order: sectionFeatures.length })} className="bg-[rgb(var(--theme-600))] text-white p-2 rounded-full hover:opacity-90 shadow-lg">
              <Plus size={20}/>
            </button>
          )}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
          {sectionFeatures.map((t, i) => (
            <div key={t.id || i} className="bg-[rgb(var(--theme-50))] p-8 rounded-3xl shadow-lg border border-[rgb(var(--theme-100))] text-center hover:-translate-y-1 transition-transform relative group/card">
              {isAdmin && (
                <div className="absolute top-3 right-3 flex gap-1 opacity-0 group-hover/card:opacity-100 transition-opacity">
                  <button onClick={() => handleFeatureEdit('selbstverteidigungskurs', t, i)} className="bg-blue-600 text-white p-1.5 rounded-full hover:bg-blue-700"><Edit2 size={14}/></button>
                  <button onClick={() => handleFeatureDelete('selbstverteidigungskurs', t, i)} className="bg-[rgb(var(--theme-600))] text-white p-1.5 rounded-full hover:opacity-90"><Trash2 size={14}/></button>
                </div>
              )}
              <div className="w-16 h-16 bg-[rgb(var(--theme-50))] rounded-2xl flex items-center justify-center text-[rgb(var(--theme-600))] mx-auto mb-5">
                {getIcon(t.icon, 28)}
              </div>
              <h4 className="font-black uppercase mb-2">{t.title}</h4>
              <p className="text-gray-900 text-sm leading-relaxed">{t.description}</p>
            </div>
          ))}
        </div>

        {(() => {
          const cta = pages['selbstverteidigungskurs-cta'] || defaultPages['selbstverteidigungskurs-cta'];
          const ctaFields = [
            { name: 'title', label: 'Überschrift', required: true },
            { name: 'content', label: 'Beschreibung', required: true },
            { name: 'subtitle', label: 'Button-Text', required: true }
          ];
          return (
            <div className="bg-[rgb(var(--theme-900))] rounded-3xl p-8 md:p-10 text-center text-white shadow-2xl relative group/cta">
              {isAdmin && (
                <button onClick={() => openModal('pages', ctaFields, 'CTA Bearbeiten', { slug: 'selbstverteidigungskurs-cta', ...cta })} className="absolute top-4 right-4 bg-blue-600 text-white p-2 rounded-full hover:bg-blue-700 shadow-lg opacity-0 group-hover/cta:opacity-100 transition-opacity z-10"><Edit2 size={16}/></button>
              )}
              <h3 className="text-xl font-black uppercase italic mb-3">{cta.title}</h3>
              <p className="text-gray-100 mb-6">{cta.content}</p>
              <button onClick={() => setActiveTab('kontakt')} className="bg-[rgb(var(--theme-600))] text-white px-8 py-3 rounded-xl font-bold uppercase hover:opacity-90 transition-colors shadow-lg shadow-red-600/20">
                {cta.subtitle || 'Jetzt anmelden'}
              </button>
            </div>
          );
        })()}
      </div>
    );
  };

  // --- KONTAKT SECTION ---
  const KontaktSection = () => {
    const page = pages['kontakt'] || defaultPages['kontakt'];
    const pageFields = [
      { name: 'title', label: 'Titel', required: true },
      { name: 'subtitle', label: 'Untertitel' },
      { name: 'content', label: 'Kontaktdaten (je Zeile: Vereinsname, Adresse, E-Mail, Telefon, @Instagram)', type: 'textarea', required: true },
      { name: 'image_url', label: 'Bild (optional)', type: 'image' }
    ];

    // Parse content lines: org name, address, email, phone, instagram, facebook
    const lines = (page.content || '').split('\n').map(l => l.trim()).filter(Boolean);
    const orgName = lines[0] || 'TuS International Bremen e.V.';
    const address = lines[1] || 'Bremen, Deutschland';
    const email = lines.find(l => l.includes('@') && !l.startsWith('@')) || 'info@tus-international.de';
    const phone = lines.find(l => l.startsWith('+')) || '+49 123 456 7890';
    const instagram = lines.find(l => l.startsWith('@')) || '';
    const facebook = lines.find(l => l.includes('facebook.com')) || '';

    return (
      <div className="max-w-6xl mx-auto px-4 py-24 relative group">
        {isAdmin && (
          <button 
            onClick={() => openModal('pages', pageFields, 'Kontakt Bearbeiten', { slug: 'kontakt', ...page })} 
            className="absolute top-8 right-4 bg-blue-600 text-white p-3 rounded-full hover:bg-blue-700 shadow-lg opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity z-10"
          >
            <Edit2 size={20}/>
          </button>
        )}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-black uppercase italic mb-4">Kontakt<span className="text-[rgb(var(--theme-600))]">ieren Sie uns</span></h2>
          <p className="text-gray-900 text-lg max-w-2xl mx-auto">{page.subtitle || 'Wir freuen uns auf Ihre Nachricht'}</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
          <a href={`tel:${phone.replace(/\s/g, '')}`} className="bg-[rgb(var(--theme-50))] p-8 rounded-3xl shadow-lg border border-[rgb(var(--theme-100))] text-center hover:-translate-y-1 transition-transform group">
            <div className="w-16 h-16 bg-[rgb(var(--theme-50))] rounded-2xl flex items-center justify-center text-[rgb(var(--theme-600))] mx-auto mb-5 group-hover:bg-[rgb(var(--theme-600))] group-hover:text-white transition-colors">
              <Phone size={28} />
            </div>
            <h4 className="font-black uppercase text-lg mb-2">Telefon</h4>
            <p className="text-[rgb(var(--theme-600))] font-bold text-lg">{phone}</p>
            <p className="text-gray-500 text-sm mt-2">Mo-Fr: 09:00 - 18:00 Uhr</p>
          </a>

          <a href={`mailto:${email}`} className="bg-[rgb(var(--theme-50))] p-8 rounded-3xl shadow-lg border border-[rgb(var(--theme-100))] text-center hover:-translate-y-1 transition-transform group">
            <div className="w-16 h-16 bg-[rgb(var(--theme-50))] rounded-2xl flex items-center justify-center text-[rgb(var(--theme-600))] mx-auto mb-5 group-hover:bg-[rgb(var(--theme-600))] group-hover:text-white transition-colors">
              <Mail size={28} />
            </div>
            <h4 className="font-black uppercase text-lg mb-2">E-Mail</h4>
            <p className="text-[rgb(var(--theme-600))] font-bold">{email}</p>
            <p className="text-gray-500 text-sm mt-2">Antwort innerhalb von 24 Stunden</p>
          </a>

          <div className="bg-[rgb(var(--theme-50))] p-8 rounded-3xl shadow-lg border border-[rgb(var(--theme-100))] text-center hover:-translate-y-1 transition-transform group">
            <div className="w-16 h-16 bg-[rgb(var(--theme-50))] rounded-2xl flex items-center justify-center text-[rgb(var(--theme-600))] mx-auto mb-5 group-hover:bg-[rgb(var(--theme-600))] group-hover:text-white transition-colors">
              <MapPin size={28} />
            </div>
            <h4 className="font-black uppercase text-lg mb-2">Adresse</h4>
            <p className="text-gray-900 font-bold">{orgName}</p>
            <p className="text-gray-500 text-sm mt-2">{address}</p>
          </div>

          {instagram && (
            <a href={`https://instagram.com/${instagram.replace('@', '')}`} target="_blank" rel="noopener noreferrer" className="bg-[rgb(var(--theme-50))] p-8 rounded-3xl shadow-lg border border-[rgb(var(--theme-100))] text-center hover:-translate-y-1 transition-transform group">
              <div className="w-16 h-16 bg-gradient-to-br from-purple-500 via-pink-500 to-orange-400 rounded-2xl flex items-center justify-center text-white mx-auto mb-5 group-hover:scale-110 transition-transform">
                <Instagram size={28} />
              </div>
              <h4 className="font-black uppercase text-lg mb-2">Instagram</h4>
              <p className="text-[rgb(var(--theme-600))] font-bold">{instagram}</p>
              <p className="text-gray-500 text-sm mt-2">Folgen Sie uns!</p>
            </a>
          )}

          {facebook && (
            <a href={facebook.startsWith('http') ? facebook : `https://${facebook}`} target="_blank" rel="noopener noreferrer" className="bg-[rgb(var(--theme-50))] p-8 rounded-3xl shadow-lg border border-[rgb(var(--theme-100))] text-center hover:-translate-y-1 transition-transform group">
              <div className="w-16 h-16 bg-[#1877F2] rounded-2xl flex items-center justify-center text-white mx-auto mb-5 group-hover:scale-110 transition-transform shadow-md">
                <Facebook size={28} fill="currentColor" />
              </div>
              <h4 className="font-black uppercase text-lg mb-2">Facebook</h4>
              <p className="text-[rgb(var(--theme-600))] font-bold">TuS International</p>
              <p className="text-gray-500 text-sm mt-2">Besuchen Sie uns!</p>
            </a>
          )}
        </div>

        {page.image_url && (
          <div className="mb-12 flex justify-center">
            <img src={page.image_url} alt={page.title} className="rounded-3xl max-w-full h-auto object-cover shadow-2xl cursor-pointer" style={{maxHeight: '400px'}} onClick={() => setLightbox({ isOpen: true, images: [page.image_url], index: 0 })} />
          </div>
        )}

        {(() => {
          const cta = pages['kontakt-cta'] || defaultPages['kontakt-cta'];
          const ctaFields = [
            { name: 'title', label: 'Überschrift', required: true },
            { name: 'content', label: 'Beschreibung', required: true }
          ];
          return (
        <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-[2rem] p-8 md:p-12 text-white shadow-2xl relative group/cta">
          {isAdmin && (
            <button onClick={() => openModal('pages', ctaFields, 'CTA Bearbeiten', { slug: 'kontakt-cta', ...cta })} className="absolute top-4 right-4 bg-blue-600 text-white p-2 rounded-full hover:bg-blue-700 shadow-lg opacity-0 group-hover/cta:opacity-100 transition-opacity z-10"><Edit2 size={16}/></button>
          )}
          <div className="max-w-2xl mx-auto text-center">
            <h3 className="text-2xl md:text-3xl font-black uppercase italic mb-4">{(() => { const w = (cta.title || '').split(' '); const last = w.pop(); return <>{w.join(' ')} <span className="text-[rgb(var(--theme-600))]"> {last}</span></>; })()}</h3>
            <p className="text-white/70 leading-relaxed mb-8">
              {cta.content}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href={`tel:${phone.replace(/\s/g, '')}`} className="bg-[rgb(var(--theme-600))] text-white px-8 py-4 rounded-xl font-bold uppercase hover:opacity-90 transition-colors flex items-center justify-center gap-2 shadow-lg shadow-red-600/30">
                <Phone size={20} /> Jetzt anrufen
              </a>
              <a href={`mailto:${email}`} className="bg-white/10 text-white px-8 py-4 rounded-xl font-bold uppercase hover:bg-white/20 transition-colors flex items-center justify-center gap-2 border border-white/20">
                <Mail size={20} /> E-Mail senden
              </a>
            </div>
          </div>
        </div>
          );
        })()}
      </div>
    );
  };

  const DynamicPageSection = ({ slug }) => {
    const page = pages[slug] || defaultPages[slug];
    if (!page) return null;

    const fields = [
      { name: 'title', label: 'Titel', required: true },
      { name: 'subtitle', label: 'Untertitel / Info', type: 'textarea' },
      { name: 'content', label: 'Inhalt', type: 'textarea', required: true },
      { name: 'image_url', label: 'Bild (optional)', type: 'image' }
    ];

    const words = page.title.split(' ');
    const firstWord = words[0];
    const restWords = words.slice(1).join(' ');

    return (
      <div className="max-w-4xl mx-auto px-4 py-24 relative group">
        {isAdmin && (
          <button 
            onClick={() => openModal('pages', fields, 'Seite Bearbeiten', { slug, ...page })} 
            className="absolute top-8 right-4 bg-blue-600 text-white p-3 rounded-full hover:bg-blue-700 shadow-lg opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity z-10"
          >
            <Edit2 size={20}/>
          </button>
        )}
        <h2 className="text-4xl md:text-5xl font-black uppercase italic mb-8 text-center">{firstWord} {restWords && <span className="text-[rgb(var(--theme-600))]">{restWords}</span>}</h2>
        
        {page.image_url && (
          <div className="mb-10 flex flex-col items-center bg-[rgb(var(--theme-50))] p-6 rounded-3xl shadow-lg border border-[rgb(var(--theme-100))]">
            <img src={page.image_url} alt={page.title} className="rounded-2xl max-w-full h-auto object-cover cursor-pointer" style={{maxHeight: '500px'}} onClick={() => setLightbox({ isOpen: true, images: [page.image_url], index: 0 })} />
            {page.subtitle && <p className="text-center text-gray-900 italic mt-6 whitespace-pre-line font-bold text-lg">{page.subtitle}</p>}
          </div>
        )}

        <div className="bg-[rgb(var(--theme-50))] p-8 md:p-10 rounded-3xl shadow-lg border border-[rgb(var(--theme-100))] text-gray-900 leading-relaxed space-y-6 whitespace-pre-line text-lg">
          {page.content}
        </div>      </div>
    );
  };

  if (!isScriptLoaded) return <div className="h-screen flex items-center justify-center font-black italic uppercase text-gray-400 bg-gray-50"><Loader2 className="animate-spin mr-3"/> Wird geladen...</div>;

  return (
    <div className="min-h-screen bg-[rgb(var(--theme-50))] font-sans">
      <Navigation />
      
      {activeTab === 'home' && (
        <>
          {(() => {
            const heroPage = pages['hero'] || defaultPages['hero'];
            const heroFields = [
              { name: 'title', label: 'Überschrift', required: true },
              { name: 'content', label: 'Untertitel', required: true },
              { name: 'image_url', label: 'Hintergrundbild', type: 'image' }
            ];
            return (
              <div className="h-[70vh] bg-gray-950 flex items-center justify-center text-center px-4 bg-cover bg-center relative group" style={{backgroundImage: `linear-gradient(rgba(0,0,0,0.7), rgba(0,0,0,0.8)), url("${heroPage.image_url || ''}")`}}>
                {isAdmin && (
                  <button 
                    onClick={() => openModal('pages', heroFields, 'Hero Bearbeiten', { slug: 'hero', ...heroPage })} 
                    className="absolute top-8 right-8 bg-blue-600 text-white p-3 rounded-full hover:bg-blue-700 shadow-lg opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity z-10"
                  >
                    <Edit2 size={20}/>
                  </button>
                )}
                {isAdmin && (
                  <button 
                    onClick={() => setShowThemePicker(!showThemePicker)} 
                    className="absolute top-8 left-8 bg-white/20 backdrop-blur-sm text-white p-3 rounded-full hover:bg-white/30 shadow-lg transition-all z-10 border border-white/30"
                  >
                    <Palette size={20}/>
                  </button>
                )}
                {showThemePicker && isAdmin && (
                  <div className="absolute top-24 left-8 bg-white rounded-3xl shadow-2xl p-6 z-50 w-80 max-h-[60vh] overflow-y-auto border border-gray-100">
                    <div className="flex justify-between items-center mb-5">
                      <h3 className="font-black uppercase text-sm tracking-wider">Farbthema wählen</h3>
                      <button onClick={() => setShowThemePicker(false)} className="text-gray-400 hover:text-amber-200"><X size={18}/></button>
                    </div>
                    <div className="space-y-3">
                      {Object.entries(THEMES).map(([key, theme]) => (
                        <button
                          key={key}
                          onClick={() => handleThemeChange(key)}
                          className={`w-full flex items-center gap-4 p-4 rounded-2xl transition-all text-left ${
                            currentTheme === key 
                              ? 'ring-2 ring-offset-2 bg-gray-50 shadow-md' 
                              : 'hover:bg-gray-50 border border-gray-100'
                          }`}
                          style={currentTheme === key ? { ringColor: theme.preview } : {}}
                        >
                          <div 
                            className="w-12 h-12 rounded-xl flex items-center justify-center text-xl shadow-inner shrink-0"
                            style={{ backgroundColor: theme.preview }}
                          >
                            <span className="drop-shadow-sm">{theme.emoji}</span>
                          </div>
                          <div className="flex-grow min-w-0">
                            <div className="font-bold text-sm flex items-center gap-2">
                              {theme.name}
                              {currentTheme === key && <span className="text-xs px-2 py-0.5 rounded-full text-white" style={{ backgroundColor: theme.preview }}>Aktiv</span>}
                            </div>
                            <p className="text-xs text-gray-400 mt-0.5 truncate">{theme.desc}</p>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                <div className="max-w-4xl">
                  <h2 className="text-5xl md:text-7xl lg:text-8xl font-black text-white uppercase italic leading-none mb-6">
                    {(() => { 
                      const words = (heroPage.title || '').split(' '); 
                      const lastWord = words.pop(); 
                      return (
                        <>
                          {words.join(' ')} 
                          <span className="text-[rgb(var(--theme-600))]"> {lastWord}</span>
                        </>
                      ); 
                    })()}
                  </h2>
                  <p className="text-white/80 uppercase tracking-[0.2em] md:tracking-[0.4em] text-xs md:text-base lg:text-lg font-bold">
                    {heroPage.content || ''}
                  </p>
                </div>
              </div>
            );
          })()}
        </>
      )}

      {activeTab === 'karate' && <DynamicPageSection slug="karate" />}
      {activeTab === 'trainingsplan' && <TrainingSection />}
      {activeTab === 'trainer' && <TrainerSection />}
      {activeTab === 'kontakt' && <KontaktSection />}
      {activeTab === 'erfolge' && <ErfolgeSection />}
      {activeTab === 'termine' && <EventsSection />}
      {activeTab === 'privattraining' && <PrivattrainingSection />}
      {activeTab === 'selbstverteidigungskurs' && <SelbstverteidigungskursSection />}

      {activeTab === 'login' && (
        <div className="max-w-md mx-auto py-20 md:py-40 px-4">
          <form onSubmit={handleLogin} className="bg-[rgb(var(--theme-50))] p-8 md:p-10 rounded-[2rem] shadow-2xl border border-[rgb(var(--theme-100))]">
            <h2 className="text-3xl font-black mb-8 uppercase italic text-center">Admin <span className="text-[rgb(var(--theme-600))]">Login</span></h2>
            <input name="email" type="email" placeholder="Email" className="w-full p-4 mb-4 bg-gray-50 rounded-xl border border-gray-100 focus:outline-none focus:ring-2 focus:ring-[rgb(var(--theme-600))] font-medium" required />
            <input name="password" type="password" placeholder="Passwort" className="w-full p-4 mb-8 bg-gray-50 rounded-xl border border-gray-100 focus:outline-none focus:ring-2 focus:ring-[rgb(var(--theme-600))] font-medium" required />
            <button className="w-full bg-[rgb(var(--theme-600))] text-white py-4 rounded-xl font-black uppercase tracking-wider hover:opacity-90 transition-colors flex justify-center items-center shadow-lg shadow-red-600/20">
              {isLoginLoading ? <Loader2 className="animate-spin"/> : 'Login'}
            </button>
            {loginError && <p className="text-[rgb(var(--theme-600))] text-sm mt-6 text-center font-bold bg-[rgb(var(--theme-50))] p-3 rounded-lg">{loginError}</p>}
          </form>
        </div>
      )}

      {/* Lightbox */}
      {lightbox.isOpen && (
        <div
          className="fixed inset-0 bg-black/95 z-[200] flex items-center justify-center p-4 backdrop-blur-sm"
          onClick={closeLightbox}
        >
          <button
            onClick={closeLightbox}
            className="absolute top-6 right-6 text-white/50 hover:text-white transition-colors p-2 hover:bg-white/10 rounded-full z-[210]"
          >
            <X size={32} />
          </button>

          {lightbox.images.length > 1 && (
            <>
              <button
                onClick={(e) => { e.stopPropagation(); prevImage(); }}
                className="absolute left-4 md:left-8 top-1/2 -translate-y-1/2 text-white/50 hover:text-white p-3 hover:bg-white/10 rounded-full transition-all z-[210]"
              >
                <ChevronLeft size={48} />
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); nextImage(); }}
                className="absolute right-4 md:right-8 top-1/2 -translate-y-1/2 text-white/50 hover:text-white p-3 hover:bg-white/10 rounded-full transition-all z-[210]"
              >
                <ChevronRight size={48} />
              </button>
              <div className="absolute bottom-8 left-1/2 -translate-x-1/2 text-white/70 font-mono text-sm bg-black/40 px-4 py-2 rounded-full border border-white/10">
                {lightbox.index + 1} / {lightbox.images.length}
              </div>
            </>
          )}

          <img
            src={lightbox.images[lightbox.index]}
            alt=""
            className="max-w-full max-h-[90vh] object-contain rounded-lg shadow-2xl select-none"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
      {/* Admin Form Modal */}
      <AdminForm 
        isOpen={modalConfig.isOpen}
        onClose={() => setModalConfig({ isOpen: false })}
        onSubmit={modalConfig.onSubmit}
        fields={modalConfig.fields || []}
        initialData={modalConfig.initialData}
        title={modalConfig.title}
        supabaseClient={supabase}
      />

      {/* Footer */}
      <footer className="bg-[rgb(var(--theme-900))] text-white mt-auto border-t border-white/10">
        {/* We use max-w-screen-2xl and px-8 to push content further left while keeping it tidy */}
        <div className="max-w-screen-2xl mx-auto px-8 md:px-12 py-16 relative group">
          
          {isAdmin && (
            <button 
              onClick={() => {
                const footerFields = [
                  { name: 'title', label: 'Vereinsname', required: true },
                  { name: 'subtitle', label: 'Beschreibung', type: 'textarea' },
                  { name: 'content', label: 'Kontaktdaten (je Zeile: Vereinsname, Adresse, E-Mail, Telefon, @Instagram)', type: 'textarea', required: true },
                  { name: 'image_url', label: 'Bild (optional)', type: 'image' }
                ];
                const kontaktPage = pages['kontakt'] || defaultPages['kontakt'];
                openModal('pages', footerFields, 'Kontakt / Footer Bearbeiten', { slug: 'kontakt', ...kontaktPage });
              }} 
              className="absolute top-4 right-4 bg-blue-600 text-white p-2 rounded-full hover:bg-blue-700 shadow-lg opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity z-10"
            >
              <Edit2 size={16}/>
            </button>
          )}

          {(() => {
            const kp = pages['kontakt'] || defaultPages['kontakt'];
            const kLines = (kp.content || '').split('\n').map(l => l.trim()).filter(Boolean);
            const kOrg = kLines[0] || 'TuS International Bremen e.V.';
            const kAddr = kLines[1] || 'Bremen, Deutschland';
            const kEmail = kLines.find(l => l.includes('@') && !l.startsWith('@')) || 'tusinterbremen@web.de';
            
            return (
              <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
                {/* Brand Section - Takes up more space on the left */}
                <div className="md:col-span-5 flex flex-col items-start">
                  <div className="flex items-center space-x-4 mb-6">
                    <div className="w-12 h-12 bg-[rgb(var(--theme-600))] rounded-full flex items-center justify-center text-white font-black italic shadow-inner">
                      TuS
                    </div>
                    <h3 className="text-2xl font-black uppercase tracking-tight">TuS International</h3>
                  </div>
                  <p className="text-gray-200 text-base leading-relaxed max-w-sm">
                    Traditionelles Shotokan Karate-Do seit 2003 in Bremen. Für Kinder, Jugendliche und Erwachsene.
                  </p>
                </div>

                {/* Membership Section */}
                <div className="md:col-span-3">
                  <h4 className="font-bold uppercase text-sm tracking-[0.2em] mb-8 text-amber-200/90">Wir sind Mitglied im:</h4>
                  <ul className="space-y-4 text-base text-gray-200">
                    <li className="flex items-start">
                      <span className="mr-2">•</span>
                      <a href="https://bremerkarateverband.de/" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">Bremer Karate Verband e.V.</a>
                    </li>
                    <li className="flex items-start">
                      <span className="mr-2">•</span>
                      <a href="https://www.karate.de/" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">Deutscher Karate Verband e.V.</a>
                    </li>
                    <li className="flex items-start">
                      <span className="mr-2">•</span>
                      <a href="https://www.lsb-bremen.de/" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">Landessportbund Bremen e.V.</a>
                    </li>
                  </ul>
                </div>

                {/* Contact Section */}
                <div className="md:col-span-4">
                  <h4 className="font-bold uppercase text-sm tracking-[0.2em] mb-8 text-amber-200/90">Kontakt</h4>
                  <div className="space-y-4 text-gray-200 text-base">
                    <p className="font-medium text-white">{kOrg}</p>
                    <p>{kAddr}</p>
                    <p>
                      <a href={`mailto:${kEmail}`} className="text-amber-100 hover:text-white hover:underline transition-colors">{kEmail}</a>
                    </p>
                    {(() => {
                      const insta = kLines.find(l => l.startsWith('@')) || '@tus_international_bremen';
                      const fb = kLines.find(l => l.includes('facebook.com')) || 'https://www.facebook.com/tusbremen';
                      return (
                        <div className="flex flex-wrap gap-6 pt-4">
                          <a href={`https://instagram.com/${insta.replace('@', '')}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-gray-200 hover:text-white transition-colors group">
                            <Instagram size={20} className="text-gray-300 group-hover:text-[rgb(var(--theme-600))]" />
                            <span className="text-base">Instagram</span>
                          </a>
                          <a href={fb.startsWith('http') ? fb : `https://${fb}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-gray-200 hover:text-white transition-colors group">
                            <Facebook size={20} className="text-gray-300 group-hover:text-[rgb(var(--theme-600))]" />
                            <span className="text-base">Facebook</span>
                          </a>
                        </div>
                      );
                    })()}
                  </div>
                </div>
              </div>
            );
          })()}

          {/* Bottom Border and Copyright */}
          <div className="border-t border-white/10 mt-16 pt-8 flex flex-col md:flex-row justify-between items-center text-gray-300 text-xs gap-4">
            <p>&copy; {new Date().getFullYear()} TuS International Bremen e.V. Alle Rechte vorbehalten.</p>
            <div className="flex space-x-6">
              <button className="hover:text-white transition-colors" onClick={() => setActiveTab('impressum')}>Impressum</button>
              <button className="hover:text-white transition-colors" onClick={() => setActiveTab('datenschutz')}>Datenschutz</button>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default App;